/* ============================================================
   MoveTableDialog — relocate a tab + all its active orders to
   a different table. Owner ask Feb 2026: "Need option to move
   table — sometimes customer changing their tables".

   Props:
     - open: bool
     - onOpenChange: (bool) => void
     - tab: { id, customer_name, table_id, current_total }
     - sourceTableNumber: string|number — current table label
     - onMoved: ({ from_table_number, to_table_number, orders_moved,
                  source_freed }) => void   // fired after success
   ============================================================ */
import React, { useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, Search, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import api from "../lib/api";

export default function MoveTableDialog({
  open,
  onOpenChange,
  tab,
  sourceTableNumber,
  onMoved,
}) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [reason, setReason] = useState("");
  const [picked, setPicked] = useState(null);
  const [moving, setMoving] = useState(false);

  // Pull the live table list every time the dialog opens so the
  // available/seated state reflects reality (multi-tab tables show up
  // as a viable destination — co-existing tabs are supported by the
  // backend).
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true);
    api
      .get("/tables")
      .then(({ data }) => {
        if (!alive) return;
        const sorted = (data || []).slice().sort(
          (a, b) => Number(a.number) - Number(b.number)
        );
        setTables(sorted);
      })
      .catch(() => {
        if (alive) toast.error("Could not load tables");
      })
      .finally(() => alive && setLoading(false));
    // Cleanup — fires when `open` flips to false OR component unmounts.
    // Reset the local form state here so the dialog re-opens fresh.
    return () => {
      alive = false;
      setSearch("");
      setReason("");
      setPicked(null);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tables
      .filter((t) => t.id !== tab?.table_id) // can't move to itself
      .filter((t) => t.status !== "out_of_service")
      .filter((t) => {
        if (!q) return true;
        const label = `t${t.number}`.toLowerCase();
        return label.includes(q) || String(t.number).includes(q);
      });
  }, [tables, search, tab]);

  const confirmMove = async () => {
    if (!tab || !picked) return;
    setMoving(true);
    try {
      const { data } = await api.post(`/tabs/${tab.id}/move`, {
        destination_table_id: picked.id,
        reason: reason.trim(),
      });
      const fromLbl = data.from_table_number ?? sourceTableNumber ?? "—";
      const toLbl = data.to_table_number ?? picked.number;
      const freed = data.source_freed ? " · Source freed" : "";
      toast.success(
        `Moved to T${toLbl} · ${data.orders_moved} order${
          data.orders_moved === 1 ? "" : "s"
        } from T${fromLbl}${freed}`,
        { duration: 7000 }
      );
      onOpenChange(false);
      onMoved && onMoved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Move failed");
    } finally {
      setMoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-bg-secondary border border-white/10 max-w-md"
        data-testid="move-table-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-white flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-gold" />
            Move Tab to Another Table
          </DialogTitle>
        </DialogHeader>
        {tab && (
          <div className="space-y-4 pt-2">
            <div className="bg-white/[0.02] border border-white/10 rounded-sm p-3 space-y-0.5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans">
                Currently on
              </p>
              <p className="text-white font-sans">
                <span className="text-gold font-serif text-lg">
                  T{sourceTableNumber ?? "—"}
                </span>
                <span className="text-zinc-400 text-sm">
                  {" "}· {tab.customer_name || "Tab"} · $
                  {Number(tab.current_total || 0).toFixed(2)}
                </span>
              </p>
            </div>

            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search table number…"
                className="pl-8 bg-transparent border border-white/10 text-white font-sans h-10"
                data-testid="move-table-search"
                autoFocus
              />
            </div>

            <div
              className="max-h-72 overflow-y-auto border border-white/5 rounded-sm divide-y divide-white/5"
              data-testid="move-table-list"
            >
              {loading && (
                <p className="text-xs text-zinc-500 font-sans p-3 text-center">
                  Loading tables…
                </p>
              )}
              {!loading && filtered.length === 0 && (
                <p className="text-xs text-zinc-500 font-sans p-3 text-center">
                  No matching tables.
                </p>
              )}
              {!loading &&
                filtered.map((t) => {
                  const isPicked = picked?.id === t.id;
                  const statusTone =
                    t.status === "available"
                      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                      : t.status === "seated"
                      ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
                      : "bg-zinc-500/15 text-zinc-300 border-zinc-500/30";
                  return (
                    <button
                      key={t.id}
                      onClick={() => setPicked(t)}
                      className={`w-full text-left px-3 py-2.5 flex items-center justify-between transition-colors ${
                        isPicked
                          ? "bg-gold/10 ring-1 ring-gold/40"
                          : "hover:bg-white/[0.03]"
                      }`}
                      data-testid={`move-table-option-${t.number}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="font-serif text-gold text-base w-10">
                          T{t.number}
                        </span>
                        <span className="text-xs text-zinc-400 font-sans">
                          {t.capacity ? `${t.capacity} seats` : ""}
                        </span>
                      </div>
                      <span
                        className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-sm border font-sans ${statusTone}`}
                      >
                        {t.status || "—"}
                      </span>
                    </button>
                  );
                })}
            </div>

            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans">
                Reason (optional)
              </label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. customer asked for outdoor table"
                className="bg-transparent border border-white/10 text-white font-sans"
                data-testid="move-table-reason"
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="flex-1 text-zinc-400 hover:text-white"
                data-testid="move-table-cancel"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                onClick={confirmMove}
                disabled={!picked || moving}
                className="flex-1 bg-gold/20 text-gold border border-gold/40 hover:bg-gold/30 rounded-sm font-sans disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="move-table-confirm"
              >
                <ArrowRightLeft className="w-4 h-4 mr-1.5" />
                {moving
                  ? "Moving…"
                  : picked
                  ? `Move to T${picked.number}`
                  : "Pick a table"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
